import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { PaymentDrawer } from '@components/organisms/PaymentDrawer'
import {
  IntentTypes,
  PaymentIntentData,
  SetupIntentData,
  StripePaymentContextType,
} from '@/@types/stripe'
import { useSetupIntent } from '@utils/useCheckout'
import config from '@/config'
import log from 'loglevel'
import {
  setSessionItem,
  getSessionItem,
  removeSessionItem,
  SessionStorageKeys,
} from '@/utils/sessionStorage'
import {
  LegalMatterQuoteFragment,
  PaymentInfo,
} from '@/@types/generated/graphql'

const stripePromise = loadStripe(config.stripeKey)

const StripePaymentContext = createContext<
  StripePaymentContextType | undefined
>(undefined)

interface StripePaymentProviderProps {
  children: ReactNode
}

export const StripePaymentProvider: React.FC<StripePaymentProviderProps> = ({
  children,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentIntent, setCurrentIntent] = useState<IntentTypes | null>(null)
  const [paymentIntentClientSecret, setPaymentIntentClientSecret] = useState<
    string | undefined
  >()
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<
    string | undefined
  >()
  const [retrievedPaymentMethodId, setRetrievedPaymentMethodId] = useState<
    string | undefined
  >()
  const [createSetupIntent] = useSetupIntent()
  const [cardInfo, setCardInfo] = useState<PaymentInfo | null>(null)
  const [setupIntent, setSetupIntent] = useState<string | undefined>()
  const [quoteKeyToPay, setQuoteKeyToPay] = useState<string | undefined>(
    undefined,
  )

  const autoContinueAfterRedirect = async () => {
    const params = new URLSearchParams(location.search)
    const clientSecret = params.get('setup_intent_client_secret')
    const redirectStatus = params.get('redirect_status')
    const setup_intent = params.get('setup_intent')

    const storageQuoteKey = getSessionItem(SessionStorageKeys.QuoteKey)

    if (!!setup_intent) {
      setSetupIntent(setup_intent)
    }
    if (!!storageQuoteKey) {
      setQuoteKeyToPay(storageQuoteKey)
    }

    if (clientSecret && redirectStatus === 'succeeded') {
      const stripe = await stripePromise
      const { setupIntent, error } =
        (await stripe?.retrieveSetupIntent(clientSecret)) ?? {}

      if (!error && setupIntent?.status === 'succeeded') {
        setSetupIntentClientSecret(clientSecret)
        setRetrievedPaymentMethodId(setupIntent.payment_method?.toString())

        // Restore the previously stored PaymentIntent clientSecret
        const storedClientSecret = getSessionItem(
          SessionStorageKeys.QuotePaymentIntent,
        )
        if (storedClientSecret) {
          setPaymentIntentClientSecret(storedClientSecret)
          setCurrentIntent(IntentTypes.PAYMENT)
          setIsDrawerOpen(true)

          removeSessionItem(SessionStorageKeys.QuotePaymentIntent)
        }
      }

      const cleanedParams = new URLSearchParams(window.location.search)
      cleanedParams.delete('setup_intent_client_secret')
      cleanedParams.delete('setup_intent')
      cleanedParams.delete('redirect_status')

      const newUrl =
        window.location.pathname +
        (cleanedParams.toString() ? `?${cleanedParams.toString()}` : '')
      window.history.replaceState({}, '', newUrl)
    }
  }

  async function fetchPaymentIntentDetails(clientSecret) {
    const stripe = await stripePromise

    if (!stripe || !clientSecret) {
      throw new Error('Stripe not loaded or client secret missing')
    }

    const result = await stripe.retrievePaymentIntent(clientSecret)

    if (result.error) {
      console.error('Error retrieving PaymentIntent:', result.error.message)
      return null
    }

    return result.paymentIntent
  }

  useEffect(() => {
    void autoContinueAfterRedirect()
  }, [location.search])

  const openPaymentDrawer = async (data: PaymentIntentData) => {
    try {
      setIsProcessing(true)
      setError(null)

      setPaymentIntentClientSecret(data.clientSecret)
      setCurrentIntent(IntentTypes.PAYMENT)
      setIsDrawerOpen(true)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create payment intent',
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const openSetupDrawer = async (data: SetupIntentData) => {
    try {
      setIsProcessing(true)
      setError(null)

      const response = await createSetupIntent({
        variables: {
          subscriberKey: data.subscriberKey,
        },
      })

      if (
        response?.data?.setupIntentCreate.__typename === 'StripeKeyResponse'
      ) {
        setSetupIntentClientSecret(response.data.setupIntentCreate.key)
        setCurrentIntent(IntentTypes.SETUP)
        setIsDrawerOpen(true)
      } else if (
        response?.data?.setupIntentCreate.__typename === 'MutationError'
      ) {
        setError(
          response?.data?.setupIntentCreate?.message ||
            'Failed to create setup intent',
        )
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create setup intent',
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    setPaymentIntentClientSecret(undefined)
    setRetrievedPaymentMethodId(undefined)
    setSetupIntentClientSecret(undefined)
    setCurrentIntent(null)
    setError(null)
  }

  const processPayment = async (): Promise<boolean> => {
    // called from within the PaymentDrawer component
    // after Stripe elements are confirmed
    return true
  }

  const value: StripePaymentContextType = {
    isDrawerOpen,
    isProcessing,
    error,
    currentIntent,
    openPaymentDrawer,
    openSetupDrawer,
    closeDrawer,
    processPayment,
    fetchPaymentIntentDetails,
    cardInfo,
    setCardInfo,
    setupIntent,
    quoteKeyToPay,
    paymentIntentClientSecret,
    setupIntentClientSecret,
    retrievedPaymentMethodId,
  }

  const elementsKey =
    paymentIntentClientSecret || setupIntentClientSecret || 'default'

  const stripeOptions =
    paymentIntentClientSecret || setupIntentClientSecret
      ? {
          clientSecret: paymentIntentClientSecret || setupIntentClientSecret,
        }
      : {
          mode: 'setup' as const,
          currency: 'usd',
        }

  return (
    <StripePaymentContext.Provider value={value}>
      {children}
      <Elements
        key={elementsKey}
        stripe={stripePromise}
        options={stripeOptions}
      >
        <PaymentDrawer />
      </Elements>
    </StripePaymentContext.Provider>
  )
}

export const useStripePaymentContext = () => {
  const context = useContext(StripePaymentContext)
  if (!context) {
    throw new Error(
      'useStripePaymentContext must be used within StripePaymentProvider',
    )
  }
  return context
}
